import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getAssetsFull } from '../tab_assets/viewServices/assets_api';

const emptyPayload = {
  locations: [],
  categories: [],
  assets: [],
  operations: [],
  returns: [],
  snapshots: [],
  snapshot_items: [],
};

const AssetsDataContext = createContext(null);

export function AssetsDataProvider({ children, enabled }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasLoadedOnceRef = useRef(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const refreshFull = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAssetsFull();
      const d = res.data || {};
      setPayload({
        locations: Array.isArray(d.locations) ? d.locations : [],
        categories: Array.isArray(d.categories) ? d.categories : [],
        assets: Array.isArray(d.assets) ? d.assets : [],
        operations: Array.isArray(d.operations) ? d.operations : [],
        returns: Array.isArray(d.returns) ? d.returns : [],
        snapshots: Array.isArray(d.snapshots) ? d.snapshots : [],
        snapshot_items: Array.isArray(d.snapshot_items) ? d.snapshot_items : [],
      });
      hasLoadedOnceRef.current = true;
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load assets.');
      setPayload({ ...emptyPayload });
      hasLoadedOnceRef.current = true;
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (hasLoadedOnceRef.current) return;
    refreshFull();
  }, [enabled, refreshFull]);

  const patchSnapshotItem = useCallback((updated) => {
    if (!updated?.id) return;
    setPayload((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        snapshot_items: prev.snapshot_items.map((it) =>
          it.id === updated.id ? { ...it, ...updated } : it
        ),
      };
    });
  }, []);

  const value = useMemo(() => {
    const data = payload || emptyPayload;
    return {
      ...data,
      loading: loading && !initialLoadDone,
      refreshing: loading && initialLoadDone,
      error,
      isReady: payload !== null,
      refreshFull,
      patchSnapshotItem,
    };
  }, [payload, loading, initialLoadDone, error, refreshFull, patchSnapshotItem]);

  return <AssetsDataContext.Provider value={value}>{children}</AssetsDataContext.Provider>;
}

export function useAssetsData() {
  const ctx = useContext(AssetsDataContext);
  if (!ctx) {
    throw new Error('useAssetsData must be used within AssetsDataProvider');
  }
  return ctx;
}

/** Optional: use in components that may render outside provider (e.g. tests). */
export function useAssetsDataOptional() {
  return useContext(AssetsDataContext);
}
